import variant from '@jitl/quickjs-singlefile-browser-release-sync';
import { ButtonActionType } from '@finxone-platform/shared/sys-config-types';
import {
  QuickJSWASMModule,
  newQuickJSWASMModuleFromVariant,
  shouldInterruptAfterDeadline,
} from 'quickjs-emscripten-core';

const MAX_SCRIPT_LENGTH = 20_000;
const MAX_DATA_LENGTH = 4 * 1024 * 1024;
const MAX_NAVIGATION_LENGTH = 2_048;
const MAX_CLIPBOARD_LENGTH = 20_000;
const MAX_ACTION_OPTIONS_LENGTH = 20_000;
const MEMORY_LIMIT_BYTES = 32 * 1024 * 1024;
const STACK_LIMIT_BYTES = 512 * 1024;
const EXECUTION_LIMIT_MS = 100;

let quickJsPromise: Promise<QuickJSWASMModule> | undefined;

export type ScriptedActionData = {
  state?: Record<string, unknown>;
  route: {
    params: Record<string, unknown>;
    queryParams: Record<string, unknown>;
    url: string;
  };
  widgetContext?: unknown;
};

export type ScriptedActionCommand =
  | {
      type: 'navigate';
      url: string;
    }
  | {
      type: 'notify';
      level: 'success' | 'info' | 'warning' | 'error';
      title: string;
      body: string;
    }
  | {
      type: 'setFormValue';
      formPath: string;
      fieldPath: string;
      value: unknown;
    }
  | {
      type: 'copyToClipboard';
      value: string;
    }
  | {
      type: 'runAction';
      actionType: string;
      navigation?: Record<string, unknown>;
    };

export class ScriptedActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScriptedActionError';
  }
}

function getQuickJs(): Promise<QuickJSWASMModule> {
  quickJsPromise ??= newQuickJSWASMModuleFromVariant(variant);
  return quickJsPromise;
}

export function validateInternalNavigationUrl(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_NAVIGATION_LENGTH) {
    throw new ScriptedActionError('navigate() requires a non-empty internal URL.');
  }

  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    /[\u0000-\u001f\u007f]/.test(value)
  ) {
    throw new ScriptedActionError('navigate() only accepts an absolute path within this application.');
  }

  return value;
}

function validateIdentifier(value: unknown, label: string): string {
  if (typeof value !== 'string' || !/^[a-zA-Z0-9_.-]{1,128}$/.test(value)) {
    throw new ScriptedActionError(
      `${label} must contain only letters, numbers, dots, dashes, or underscores.`,
    );
  }
  return value;
}

function validateNotification(level: unknown, title: unknown, body: unknown) {
  const allowedLevels = ['success', 'info', 'warning', 'error'] as const;
  if (!allowedLevels.includes(level as (typeof allowedLevels)[number])) {
    throw new ScriptedActionError(`notify() level must be one of: ${allowedLevels.join(', ')}.`);
  }
  if (typeof title !== 'string' || title.length > 120) {
    throw new ScriptedActionError('notify() title cannot exceed 120 characters.');
  }
  if (typeof body !== 'string' || body.length === 0 || body.length > 1_000) {
    throw new ScriptedActionError('notify() requires a body between 1 and 1000 characters.');
  }
  return { level: level as (typeof allowedLevels)[number], title, body };
}

export function validateClipboardValue(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_CLIPBOARD_LENGTH) {
    throw new ScriptedActionError(
      `copyToClipboard() requires text between 1 and ${MAX_CLIPBOARD_LENGTH} characters.`,
    );
  }
  return value;
}

export function createClipboardCommand(value: unknown): ScriptedActionCommand {
  return { type: 'copyToClipboard', value: validateClipboardValue(value) };
}

const sensitiveStateKey =
  /(^|[_-])(access|refresh|id)?token$|password|passcode|secret|api[_-]?key|authorization|cookie|cvv|pin$/i;

export function createScriptedActionState(snapshot: unknown): Record<string, unknown> {
  const ancestors: object[] = [];
  const serialized = JSON.stringify(snapshot, function (key, value: unknown) {
    if (key && sensitiveStateKey.test(key)) return '[REDACTED]';
    if (typeof value === 'bigint') return value.toString();
    if (typeof value === 'function' || typeof value === 'symbol') return undefined;
    if (value && typeof value === 'object') {
      while (ancestors.length && ancestors[ancestors.length - 1] !== this) ancestors.pop();
      if (ancestors.includes(value)) return undefined;
      ancestors.push(value);
    }
    return value;
  });

  if (!serialized) return {};
  return JSON.parse(serialized) as Record<string, unknown>;
}

const navigationOutcomes = new Set([
  'success',
  'failure',
  'processing',
  'incomplete',
  'alternate_1',
  'alternate_2',
  'alternate_3',
  'product_unavailable',
]);

function validateNavigationOverride(value: unknown): Record<string, unknown> | undefined {
  if (value === undefined) return undefined;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ScriptedActionError('runAction() navigation must be an outcome configuration object.');
  }

  const serialized = JSON.stringify(value);
  if (!serialized || serialized.length > MAX_ACTION_OPTIONS_LENGTH) {
    throw new ScriptedActionError('runAction() navigation is too large or is not JSON-serializable.');
  }
  const navigation = JSON.parse(serialized) as Record<string, Record<string, unknown>>;

  for (const [outcome, config] of Object.entries(navigation)) {
    if (!navigationOutcomes.has(outcome) || !config || typeof config !== 'object' || Array.isArray(config)) {
      throw new ScriptedActionError(`runAction() contains an invalid navigation outcome: ${outcome}.`);
    }
    if (config['externalUrl']) {
      throw new ScriptedActionError('runAction() does not allow external navigation URLs.');
    }
    if (config['selectedNavigationOption'] === 'use-external-link') {
      throw new ScriptedActionError('runAction() does not allow external navigation options.');
    }
    const pageUrl = config['selectedPageUrl'];
    if (
      typeof pageUrl === 'string' &&
      (pageUrl.length > MAX_NAVIGATION_LENGTH ||
        pageUrl.startsWith('//') ||
        /^[a-z][a-z\d+.-]*:/i.test(pageUrl) ||
        pageUrl.includes('\\') ||
        /[\u0000-\u001f\u007f]/.test(pageUrl))
    ) {
      throw new ScriptedActionError(`runAction() contains an unsafe page URL for ${outcome}.`);
    }
  }

  return navigation;
}

export function validateRunActionOptions(
  actionTypeValue: unknown,
  optionsValue: unknown,
): { actionType: string; navigation?: Record<string, unknown> } {
  const actionType = validateIdentifier(actionTypeValue, 'Action type');
  if (!Object.values(ButtonActionType).includes(actionType as ButtonActionType)) {
    throw new ScriptedActionError(`runAction() requested an unknown action: ${actionType}.`);
  }
  if (actionType === ButtonActionType.SCRIPTED_ACTION) {
    throw new ScriptedActionError('runAction() cannot invoke another scripted action.');
  }
  if (optionsValue === undefined) return { actionType };
  if (!optionsValue || typeof optionsValue !== 'object' || Array.isArray(optionsValue)) {
    throw new ScriptedActionError('runAction() options must be an object.');
  }
  const options = optionsValue as Record<string, unknown>;
  const unsupportedKeys = Object.keys(options).filter((key) => key !== 'navigation');
  if (unsupportedKeys.length) {
    throw new ScriptedActionError(`runAction() received unsupported options: ${unsupportedKeys.join(', ')}.`);
  }
  return { actionType, navigation: validateNavigationOverride(options['navigation']) };
}

function formatGuestError(value: unknown): string {
  if (value && typeof value === 'object') {
    const error = value as { message?: unknown; name?: unknown };
    const name = typeof error.name === 'string' ? error.name : 'Error';
    const message = typeof error.message === 'string' ? error.message : JSON.stringify(value);
    return `${name}: ${message}`;
  }
  return String(value);
}

export async function executeScriptedAction(
  script: string,
  data: ScriptedActionData,
): Promise<ScriptedActionCommand[]> {
  if (typeof script !== 'string' || script.trim().length === 0) {
    throw new ScriptedActionError('No action script has been configured.');
  }
  if (script.length > MAX_SCRIPT_LENGTH) {
    throw new ScriptedActionError(`Action scripts cannot exceed ${MAX_SCRIPT_LENGTH} characters.`);
  }

  let serializedData: string;
  try {
    serializedData = JSON.stringify(data);
  } catch {
    throw new ScriptedActionError('The action data could not be serialized for the sandbox.');
  }
  if (serializedData.length > MAX_DATA_LENGTH) {
    throw new ScriptedActionError(`Action data cannot exceed ${MAX_DATA_LENGTH} serialized characters.`);
  }

  const QuickJS = await getQuickJs();
  const runtime = QuickJS.newRuntime();
  runtime.setMemoryLimit(MEMORY_LIMIT_BYTES);
  runtime.setMaxStackSize(STACK_LIMIT_BYTES);
  runtime.setInterruptHandler(shouldInterruptAfterDeadline(Date.now() + EXECUTION_LIMIT_MS));
  const context = runtime.newContext();
  const commands: ScriptedActionCommand[] = [];

  const addCommand = (command: ScriptedActionCommand) => {
    if (commands.length >= 10) {
      throw new ScriptedActionError('An action script cannot request more than 10 commands.');
    }
    commands.push(command);
  };

  const navigateHandle = context.newFunction('navigate', (urlHandle) => {
    const url = validateInternalNavigationUrl(context.dump(urlHandle));
    if (commands.some((command) => command.type === 'navigate')) {
      throw new ScriptedActionError('Only one navigation may be requested by an action script.');
    }
    addCommand({ type: 'navigate', url });
    return context.undefined;
  });
  const notifyHandle = context.newFunction('notify', (levelHandle, titleHandle, bodyHandle) => {
    const titleOrBody = context.dump(titleHandle);
    const notification = validateNotification(
      context.dump(levelHandle),
      bodyHandle ? titleOrBody : '',
      bodyHandle ? context.dump(bodyHandle) : titleOrBody,
    );
    addCommand({ type: 'notify', ...notification });
    return context.undefined;
  });
  const setFormValueHandle = context.newFunction(
    'setFormValue',
    (formPathHandle, fieldPathHandle, valueHandle) => {
      const formPath = validateIdentifier(context.dump(formPathHandle), 'Form path');
      const fieldPath = validateIdentifier(context.dump(fieldPathHandle), 'Field path');
      const value = context.dump(valueHandle);
      const serializedValue = JSON.stringify(value);
      if (serializedValue === undefined) {
        throw new ScriptedActionError('setFormValue() requires a JSON-serializable value.');
      }
      addCommand({ type: 'setFormValue', formPath, fieldPath, value: JSON.parse(serializedValue) });
      return context.undefined;
    },
  );
  const copyToClipboardHandle = context.newFunction('copyToClipboard', (valueHandle) => {
    addCommand(createClipboardCommand(context.dump(valueHandle)));
    return context.undefined;
  });
  const runActionHandle = context.newFunction('runAction', (actionTypeHandle, optionsHandle) => {
    const invocation = validateRunActionOptions(
      context.dump(actionTypeHandle),
      optionsHandle ? context.dump(optionsHandle) : undefined,
    );
    addCommand({ type: 'runAction', ...invocation });
    return context.undefined;
  });

  const consoleLogHandle = context.newFunction('log', (...valueHandles) => {
    const body = valueHandles.map((handle) => context.dump(handle)).join(' ');
    addCommand({ type: 'notify', level: 'info', title: 'Script log', body: body.slice(0, 1_000) });
    return context.undefined;
  });

  try {
    context.setProp(context.global, 'navigate', navigateHandle);
    context.setProp(context.global, 'notify', notifyHandle);
    context.setProp(context.global, 'setFormValue', setFormValueHandle);
    context.setProp(context.global, 'copyToClipboard', copyToClipboardHandle);
    context.setProp(context.global, 'runAction', runActionHandle);
    const consoleHandle = context.newObject();
    context.setProp(consoleHandle, 'log', consoleLogHandle);
    context.setProp(context.global, 'console', consoleHandle);
    consoleHandle.dispose();
    const source = `
      'use strict';
      const deepFreeze = (value) => {
        if (value && typeof value === 'object' && !Object.isFrozen(value)) {
          Object.freeze(value);
          for (const key of Object.keys(value)) deepFreeze(value[key]);
        }
        return value;
      };
      const data = deepFreeze(JSON.parse(${JSON.stringify(serializedData)}));
      (() => {
        ${script}
      })();
    `;
    const result = context.evalCode(source, 'scripted-action.js');

    const errorHandle = result.error;
    if (errorHandle) {
      const guestError = formatGuestError(context.dump(errorHandle));
      errorHandle.dispose();
      throw new ScriptedActionError(`Script execution failed: ${guestError}`);
    }
    result.value?.dispose();
    return commands;
  } finally {
    navigateHandle.dispose();
    notifyHandle.dispose();
    setFormValueHandle.dispose();
    copyToClipboardHandle.dispose();
    runActionHandle.dispose();
    consoleLogHandle.dispose();
    context.dispose();
    runtime.dispose();
  }
}
